import { useEffect, useState } from 'react'
import { HashRouter, Routes, Route, Navigate, useSearchParams, useLocation, matchPath } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { loadMe, type Me } from './lib/me'
import { HubLangContext, hubLangFor, htmlLangFor } from './lib/i18n'
import { authTransition, shouldClearWhileResolving, type Resolved } from './lib/authTransition'
import { SignIn } from './routes/SignIn'
import { NoAccess } from './routes/NoAccess'
import { Home } from './routes/Home'
import { Admin } from './routes/Admin'
import { AdminHome } from './routes/AdminHome'
import { AdminNotices } from './routes/AdminNotices'
import { Item } from './routes/Item'
import { AdminShop } from './routes/AdminShop'
import { AdminStars } from './routes/AdminStars'
import { PhotoRoom } from './routes/PhotoRoom'
import { PhotoReviews } from './routes/PhotoReviews'
import { Contests } from './routes/Contests'
import { Moderation } from './routes/Moderation'
import { AdminAudit } from './routes/AdminAudit'
import { AdminUsage } from './routes/AdminUsage'
import { startUsageCounting } from './lib/usage'
import { Wiki } from './routes/Wiki'
import { Stars } from './routes/Stars'
import { Profile } from './routes/Profile'
import { Help } from './routes/Help'
import { Guides, ShopGuideRedirect } from './routes/Guides'
import { Support } from './routes/Support'
import { SupportThreadRoute } from './routes/SupportThread'
import { SupportAskOne } from './routes/SupportAskOne'
import { SupportOneQuestion } from './routes/SupportOneQuestion'
import { Collect } from './routes/Collect'
import { Unsubscribe } from './routes/Unsubscribe'
import { Surveys } from './routes/Surveys'
import { SurveyPresent } from './routes/SurveyPresent'
import { Terms } from './routes/Terms'
import { TermsGate } from './components/TermsGate'
import { resetStarPulse } from './lib/starPulse'
import { confirmViewAs } from './lib/viewAs'
import { ViewAsBanner } from './components/ViewAsBanner'
import { AskOneSessionProvider } from './components/AskOneSession'
import { clearAskSession } from './lib/askOneSession'

/**
 * Routing. THE ARGUMENT IS OVER — react-router, David's call, 14 Sep (OPS-35).
 *
 * This file carried a long note saying the trigger to take a router was the
 * first route needing a PATH PARAMETER, and telling the next person not to add
 * a fifth flat route and re-argue it. #/stars is that fifth route, so the note
 * did its job and is now history rather than instruction. What is left is the
 * part still worth knowing:
 *
 * ── STILL HASH ROUTING, AND THAT IS NOT LAZINESS ────────────────────────────
 *
 * HashRouter, not BrowserRouter. Netlify serves this as a static site, so a
 * real path deep-links to a file that does not exist and 404s on refresh
 * unless a redirect rule exists in netlify.toml — where the Base and publish
 * settings have already produced one 404 that looked like a broken deploy.
 * Every link, bookmark and notice in the app is already a #/ hash, including
 * the Brand Change FAQ notice that went out to ~900 people. Switching to
 * history routing is a separate decision with a redirect rule attached; it is
 * not a tidy-up.
 *
 * ── WHY A LIBRARY WON IN THE END ────────────────────────────────────────────
 *
 * Not bundle size and not features — nothing here needs loaders, nested
 * layouts or data routers, and they should not start being used because they
 * are available. It is that the parameter case is where a hand-rolled table
 * starts costing more than it saves, and that every developer who ever joins
 * this project already knows this API. OPS-16 is satisfied because it bundles;
 * it is not fetched at runtime.
 *
 * ── THERE IS NO /wiki/:id YET, AND THAT IS DELIBERATE ───────────────────────
 *
 * The permalink is what made the parameter case concrete and it is NOT built
 * here: Wiki.tsx is 1,184 lines and loading one answer by id is real work, not
 * a route line. A route that accepts an id and ignores it is worse than no
 * route — it is a link that looks like it works. Filed as WIK-17; the router
 * is now in place so it is a small change when somebody wants it.
 *
 * ── THE FALLBACK IS A REDIRECT, NOT A 404 ───────────────────────────────────
 *
 * Unchanged behaviour, kept on purpose: a stale bookmark to a renamed section
 * should land somebody on the hub, not on an error screen. `replace` so the
 * dead hash does not sit in history waiting for a Back press.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'signedOut' }
  | { kind: 'signedIn'; me: Me; authUserId: string }
  // Known, signed in, no ACTIVE membership: a leaver or a deactivation (8 Oct).
  | { kind: 'inactive'; locale: string | null; signedInAs: string | null }
  | { kind: 'unlinked'; email: string }
  | { kind: 'error'; message: string }

/**
 * Who a session signed in as, for the prefilled support email on NoAccess
 * (lib/supportMail.ts). GoTrue stores a phone without its '+'.
 */
function signedInAs(session: Session | null): string | null {
  const u = session?.user
  if (u?.email) return u.email
  return u?.phone ? `+${u.phone.replace(/^\+/, '')}` : null
}

export default function App() {
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    let live = true

    // The auth user id already resolved. See lib/authTransition.ts — the
    // decision of what an auth event means lives there as a pure function
    // because it is the whole of OPS-28 and it needs a test.
    let resolved: Resolved = undefined
    // resolve() can now be called several times, so a single boolean cannot
    // say whether a given in-flight loadMe is still the current one.
    let seq = 0

    async function resolve(session: Session | null) {
      if (!live) return
      const next = authTransition(resolved, session?.user.id ?? null)

      // A token refresh, a tab re-focus, a USER_UPDATED. The session object is
      // new, the identity is not, and nothing downstream reads the access
      // token through this state. Do not touch the UI.
      if (next.kind === 'ignore') return

      resolved = next.kind === 'signedOut' ? null : next.authUserId
      const mine = ++seq

      if (next.kind === 'signedOut') {
        setState({ kind: 'signedOut' })
        return
      }

      if (shouldClearWhileResolving()) setState({ kind: 'loading' })

      try {
        // 176. A View as session this tab remembers is checked with the
        // server BEFORE anything is read, so a session that ended elsewhere
        // never renders a screen as somebody else.
        await confirmViewAs()
        const me = await loadMe(next.authUserId)
        if (!live || mine !== seq) return
        // A session with no person behind it. See the note in lib/me.ts —
        // this is the state that otherwise presents as an empty database.
        setState(
          !me
            ? { kind: 'unlinked', email: session?.user.email ?? 'unknown address' }
            : me.tenants.length === 0
              ? { kind: 'inactive', locale: me.locale, signedInAs: signedInAs(session) }
              : { kind: 'signedIn', me, authUserId: next.authUserId },
        )
      } catch (e) {
        if (live && mine === seq) setState({ kind: 'error', message: (e as Error).message })
      }
    }

    // Both of these fire on mount — getSession() resolves, and the listener
    // emits INITIAL_SESSION — so before the resolvedFor guard above, every cold
    // load ran loadMe twice concurrently. It is kept rather than dropped
    // because INITIAL_SESSION is the newer of the two behaviours and the guard
    // makes the duplicate free.
    supabase.auth.getSession().then(({ data }) => resolve(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // REC-57: the "+25 stars" baseline belongs to the person who was signed
      // in. Without this the next person on the same tab would be shown the
      // difference between two people's balances.
      if (event === 'SIGNED_OUT') resetStarPulse()
      // 1 Nov: and the Ask One conversation this tab kept is theirs too.
      if (event === 'SIGNED_OUT') clearAskSession()
      resolve(session)
    })

    return () => {
      live = false
      sub.subscription.unsubscribe()
    }
  }, [])

  // 301 (David, 19 Oct): how the hub is used. Once signed in, this device adds
  // one to today's company-wide visit count and this hour's open count, each
  // only if it has not already (lib/usage.ts decides; nothing names anybody).
  const signedIn = state.kind === 'signedIn'
  useEffect(() => (signedIn ? startUsageCounting() : undefined), [signedIn])

  // The router wraps EVERY state, not just the signed-in one, so useNavigate
  // works anywhere in the tree and the router does not unmount and remount
  // across a sign-in. The auth effect above is outside it and keeps its []
  // deps, so navigation never re-runs loadMe — which is the OPS-28 failure in
  // a new costume and the thing to watch if this file is ever restructured.
  return (
    <HashRouter>
      {/* 176. Above every state but signed-out, so the way back is always on
          screen — even behind the terms gate or the no-access page. */}
      {state.kind !== 'signedOut' && state.kind !== 'loading' && <ViewAsBanner />}
      <Screen state={state} />
    </HashRouter>
  )
}

function Screen({ state }: { state: State }) {
  // REC-49. THE ONE ROUTE ABOVE THE AUTH GATE, matched before `state` is read,
  // so the scanned page renders identically signed out, signed in, loading,
  // unlinked or in error — the person on the shelf may hold no account at all
  // (B21). It never receives `me`. Read routes/Collect.tsx's header before
  // adding a second route here: each one is a new signed-out surface.
  const path = useLocation().pathname
  const collect = matchPath('/collect/:code', path)
  if (collect) return <Collect code={collect.params.code ?? ''} />
  // 327 (ACC-53). The second route above the gate: stopping the Monday email
  // must work for somebody who has never signed in. Same rules as Collect -
  // see routes/Unsubscribe.tsx's header before adding a third.
  const unsubscribe = matchPath('/unsubscribe/:token', path)
  if (unsubscribe) return <Unsubscribe token={unsubscribe.params.token ?? ''} />

  switch (state.kind) {
    case 'loading':
      return <div className="splash" aria-busy="true">Loading…</div>

    case 'signedOut':
      return <SignIn />

    case 'inactive':
      return <NoAccess locale={state.locale} signedInAs={state.signedInAs} />

    case 'signedIn':
      return (
        // SHL-25, the UI half. ONE provider, above every route, so a screen
        // cannot forget the language the way eight routes forgot to pass
        // `locale` to AppShell. See lib/i18n.ts.
        // 165: TermsGate sits between the provider and the routes — the terms
        // of use are agreed before ANY route renders, in the reader's language,
        // and it provides Star One's agreement state to everything below.
        // 1 Nov (HLP-13's session half): the Ask One conversation, ONCE, above
        // the routes - every route renders its own AppShell, so a conversation
        // held in the shell died when a link changed the route. Keyed by the
        // signed-in ACCOUNT (the real person even under View as), so a
        // different person starts with nothing.
        <AskOneSessionProvider key={state.authUserId} owner={state.authUserId}>
        <HubLangRoot locale={state.me.locale}>
        <TermsGate>
        <Routes>
          <Route path="/" element={<Home me={state.me} />} />
          {/* SHL-33 (11 Oct): #/admin is the admin home, a page of cards; People
              and roles moved to #/admin/people, and Notices left the hub for
              #/admin/notices. */}
          <Route path="/admin" element={<AdminHome me={state.me} />} />
          <Route path="/admin/people" element={<Admin me={state.me} />} />
          <Route path="/admin/notices" element={<AdminNotices me={state.me} />} />
          {/* 154. People and roles' second section: grant suggestions. */}
          <Route path="/admin/suggestions" element={<Admin me={state.me} tab="suggestions" />} />
          {/* The first nested path in the app, and it is a section rather than
              a parameter — #/admin is People and roles, #/admin/shop is Shop
              and stock, and they are gated on different permissions because
              they are different acts (021). No shared layout element: they
              render the same AppShell with a different `current`, and a
              <Route element> wrapper would buy nothing while making the two
              screens' data loading somebody else's problem. */}
          <Route path="/admin/shop" element={<AdminShop me={state.me} tab="stock" />} />
          {/* Shop and stock has three sections of its own (074). Stock rooms
              used to stack above everything, and the sixteen rooms 071 seeded
              turned "a bit tall" into the whole screen — 072's shape again, a
              layout that was fine because of a DATA fact and stopped being fine
              when the data moved. Routes rather than component state, for the
              same three reasons /stars/store is: the Back button, a linkable
              section, and a shelf that is not read for somebody who came to fix
              a price. */}
          <Route path="/admin/shop/items" element={<AdminShop me={state.me} tab="items" />} />
          {/* REC-65: an item's tray is a route, so Back closes it and a link
              opens it. The same AdminShop — the tray sits over the list. */}
          <Route path="/admin/shop/items/:itemId" element={<AdminShop me={state.me} tab="items" />} />
          <Route path="/admin/shop/rooms" element={<AdminShop me={state.me} tab="rooms" />} />
          {/* 339 (HLP-45): the store guides moved to #/help/guides; the old
              routes, and their ?s= deep links, redirect. */}
          <Route path="/admin/shop/guide" element={<ShopGuideRedirect admin={false} />} />
          <Route path="/admin/shop/guide/admin" element={<ShopGuideRedirect admin />} />
          {/* Star One admin (9 Oct): what activities earn, and the endorsement
              words. Two routes for the same reasons as the shop's sections; the
              server refuses every read without stars.admin. */}
          <Route path="/admin/stars" element={<AdminStars me={state.me} tab="earning" />} />
          <Route path="/admin/stars/endorsements" element={<AdminStars me={state.me} tab="endorsements" />} />
          <Route path="/moderation" element={<Moderation me={state.me} />} />
          {/* 196 (ACC-8). audit.read; the page and audit_log() both check. */}
          <Route path="/admin/audit" element={<AdminAudit me={state.me} />} />
          {/* 301. How the hub is used - whole-company analytics.read; usage_dashboard() checks. */}
          <Route path="/admin/usage" element={<AdminUsage me={state.me} />} />
          {/* 145 (FED-48). The photo room — entered photos, for photo.room holders.
              A flat route: it is one list, and a photo's own page is #/item/:id.
              Both functions behind it refuse without the permission. */}
          {/* 10 Oct (David): the photo room is Photos → Gallery. The old
              address still lands there, so a bookmark does not fall through. */}
          <Route path="/photos/gallery" element={<PhotoRoom me={state.me} />} />
          {/* 152. Photo review requests: Gallery admins, and whoever a review was handed to. */}
          <Route path="/photos/reviews" element={<PhotoReviews me={state.me} />} />
          <Route path="/photos" element={<Navigate to="/contests" replace />} />
          <Route path="/photo-room" element={<Navigate to="/photos/gallery" replace />} />
          {/* 146 made contests a section of the room; 147 moved them to their
              own page, for everybody (#/contests, #/contests/:id). The old
              tab's address sends its organisers there. */}
          <Route path="/photo-room/contests" element={<Navigate to="/contests" replace />} />
          <Route path="/contests" element={<Contests me={state.me} />} />
          <Route path="/contests/:id" element={<Contests me={state.me} />} />
          <Route path="/wiki" element={<WikiRoute me={state.me} />} />
          <Route path="/stars" element={<Stars me={state.me} tab="wall" />} />
          {/* The second nested section, same shape as /admin/shop and for the
              same reason: Wall and Store are two views of Star One, and making
              them routes rather than component state gives the Back button,
              a linkable Store for REC-22's rail widget to point at (SHL-13),
              and a wall that is not fetched for somebody who came to spend. */}
          <Route path="/stars/store" element={<Stars me={state.me} tab="store" />} />
          {/* REC-47 / 099, 128. Star One admin was a third Star One tab here
              until 9 Oct, when it moved under Administration (AdminStars.tsx).
              The old path still lands on it, so a bookmark or a link in a
              note does not fall through to the hub. */}
          <Route path="/stars/admin" element={<Navigate to="/admin/stars" replace />} />
          {/* SHL-14. The route SHL-9 never got, and the reason the identity
              menu's "no profile entry, deliberately" note is now history. It is
              NOT in NAV: the tabs are where you go and this is who you are, and
              a fourth tab would spend a quarter of SHL-18's bottom bar on a
              screen most people open once. */}
          <Route path="/profile" element={<Profile me={state.me} />} />
          {/* 337 (HLP-43). Help: a ticket, feedback, your tickets. */}
          <Route path="/help" element={<Help me={state.me} />} />
          {/* 339 (HLP-45). Static segments beat params: nothing else lives under
              /help, so /help/guides is not shadowed and does not shadow. */}
          <Route path="/help/guides" element={<Guides me={state.me} />} />
          <Route path="/help/guides/:guide" element={<Guides me={state.me} />} />
          <Route path="/help/guides/:guide/:section" element={<Guides me={state.me} />} />
          {/* 165. The terms of use and the Star One terms, from the identity
              menu. Flat and not in NAV, like #/profile. */}
          <Route path="/terms" element={<Terms me={state.me} />} />
          {/* HLP-3. Its own route for 021's reason: answering a private
              question put to your lane is neither administering people nor
              moderating a colleague's speech, and 085 gave the three support
              roles none of publish, audience or moderate. Somebody may hold a
              lane and nothing else, so folding it into #/admin or
              #/moderation would gate it behind a power its holders lack. */}
          <Route path="/support" element={<Support me={state.me} />} />
          {/* HLP-22 — THE PATH-PARAMETER CASE, TAKEN. CLAUDE.md has carried
              "still not taken; do not add a fifth flat route and re-argue it"
              since WIK-17 and FED-18b both wanted it, and this is the first
              thing that could not be built without it: the bell links on the
              notification KIND, so `support.*` needed a destination that
              carries the id it was handed. A flat route ignoring the id would
              be the same "nowhere to go" fault wearing a link.

              Static segments win over params in react-router, so a
              `/support/<word>` route shadows exactly that word and nothing
              else — `ask-one` (126) is one, and no question id can be it,
              because ids are uuids. */}
          {/* 126 (HLP-35). Ask One on the desk — a section of #/support for
              analytics.read. Declared BEFORE `:id` for the reader; the router
              ranks static over param either way. */}
          <Route path="/support/ask-one" element={<SupportAskOne me={state.me} />} />
          <Route path="/support/one-question" element={<SupportOneQuestion me={state.me} />} />
          <Route path="/support/:id" element={<SupportThreadRoute me={state.me} />} />
          {/* 125 (HLP-23). One post or Wiki One item, the door a bell row
              opens. The path-parameter case again, never a flat route. */}
          <Route path="/item/:id" element={<Item me={state.me} />} />
          {/* 310 (David, 25 Oct). The QR code on the screen points at
              /surveys/:id; scanned signed out, the sign-in screen keeps the
              hash and the person lands here once their code is in. */}
          <Route path="/surveys" element={<Surveys me={state.me} view="list" />} />
          <Route path="/surveys/:id" element={<Surveys me={state.me} view="answer" />} />
          <Route path="/surveys/:id/edit" element={<Surveys me={state.me} view="edit" />} />
          <Route path="/surveys/:id/results" element={<Surveys me={state.me} view="results" />} />
          <Route path="/surveys/:id/present" element={<SurveyPresent />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </TermsGate>
        </HubLangRoot>
        </AskOneSessionProvider>
      )

    case 'unlinked':
      return (
        <Fallback title="Your sign-in is not linked to a person">
          <p>
            You are authenticated as <strong>{state.email}</strong>, but no
            platform identity is attached to that session, so nothing is
            readable. This is a data problem, not a password problem — send this
            screen to the build team.
          </p>
        </Fallback>
      )

    case 'error':
      return (
        <Fallback title="Something went wrong">
          <p className="mono">{state.message}</p>
        </Fallback>
      )
  }
}

/**
 * The query string stays a query string. `?q=` is read here and handed to the
 * screen as an INITIAL value — Wiki.tsx owns the box once the person is in it,
 * and re-imposing the URL on every render would fight anybody who typed
 * something else. Taking the router did not change that; it only means the
 * reading is `useSearchParams` rather than a hand-parsed hash.
 */
function WikiRoute({ me }: { me: Me }) {
  const [searchParams] = useSearchParams()
  return (
    <Wiki
      me={me}
      initialQuery={searchParams.get('q') ?? ''}
      // 125. `?open=<id>` is a bell row's destination: that one item, its
      // thread open, above the board.
      initialOpen={searchParams.get('open') ?? undefined}
    />
  )
}

/**
 * The signed-in tree's language, and `<html lang>` to match.
 *
 * `<html lang>` is set here as well as on the sign-in screen because a screen
 * reader picks its voice from it — a Spanish page announced by an English
 * voice is unintelligible — and because the browser's own translate prompt
 * reads it. Reset on unmount so signing out hands the sign-in screen a clean
 * attribute to set from the browser (SignIn.tsx does the same in reverse).
 */
function HubLangRoot({ locale, children }: { locale: string; children: React.ReactNode }) {
  const lang = hubLangFor(locale)
  useEffect(() => {
    const el = document.documentElement
    const before = el.getAttribute('lang')
    el.setAttribute('lang', htmlLangFor(lang))
    return () => {
      if (before === null) el.removeAttribute('lang')
      else el.setAttribute('lang', before)
    }
  }, [lang])
  return <HubLangContext.Provider value={lang}>{children}</HubLangContext.Provider>
}

function Fallback({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="signin">
      <div className="signin__card">
        <h1 className="signin__title">{title}</h1>
        <div className="signin__lede">{children}</div>
        <button className="btn btn--primary" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </div>
    </main>
  )
}
